/**
 * Layout principal com barra lateral de navegação por papel.
 * Recebe `superadmin` para renderizar o nav de super-admin com visual diferenciado.
 */
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import { PlanBanner } from './PlanBanner'
import type { Role } from '../types/api'

const ROLE_LABELS: Record<Role, string> = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN_TENANT: 'Administrador',
  GESTOR_RH: 'Gestor RH',
  OPERADOR: 'Operador',
  FUNCIONARIO: 'Funcionário',
}

interface NavItem { label: string; to: string }

const NAV_ITEMS: Partial<Record<Role, NavItem[]>> = {
  ADMIN_TENANT: [
    { label: 'Visão Geral', to: '/admin' },
    { label: 'Empresas', to: '/admin/empresas' },
    { label: 'REPs', to: '/admin/reps' },
    { label: 'Funcionários', to: '/admin/funcionarios' },
    { label: 'Usuários', to: '/admin/usuarios' },
    { label: 'Folha de Ponto', to: '/rh/espelho-mensal' },
    { label: 'Espelho (lista)', to: '/rh/espelho' },
    { label: 'Tratamento', to: '/rh/tratamento' },
    { label: 'Banco de Horas', to: '/rh/banco-horas' },
    { label: 'Feriados', to: '/admin/feriados' },
    { label: 'Políticas CLT', to: '/admin/politicas' },
    { label: 'Escalas', to: '/admin/escalas' },
    { label: 'Plano & Billing', to: '/admin/billing' },
  ],
  GESTOR_RH: [
    { label: 'Visão Geral', to: '/rh' },
    { label: 'Folha de Ponto', to: '/rh/espelho-mensal' },
    { label: 'Espelho (lista)', to: '/rh/espelho' },
    { label: 'Tratamento', to: '/rh/tratamento' },
    { label: 'Banco de Horas', to: '/rh/banco-horas' },
    { label: 'Feriados', to: '/admin/feriados' },
    { label: 'Políticas CLT', to: '/admin/politicas' },
    { label: 'Escalas', to: '/admin/escalas' },
  ],
  OPERADOR: [
    { label: 'Espelho de Ponto', to: '/rh/espelho' },
  ],
  FUNCIONARIO: [
    { label: 'Meu Ponto', to: '/funcionario' },
    { label: 'Minhas Solicitações', to: '/funcionario/solicitacoes' },
  ],
  SUPER_ADMIN: [
    { label: 'Dashboard', to: '/superadmin' },
    { label: 'Tenants', to: '/superadmin/tenants' },
  ],
}

interface Props {
  superadmin?: boolean
}

export function Layout({ superadmin = false }: Props) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()

  const navItems = user ? (NAV_ITEMS[user.role] ?? []) : []
  const accent = superadmin ? 'var(--color-warning)' : 'var(--color-primary)'

  function handleLogout() {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <aside
        style={{
          width: 'var(--sidebar-width)',
          backgroundColor: 'var(--color-gray-900)',
          color: 'var(--color-white)',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
        }}
      >
        {/* Logo */}
        <div
          style={{
            padding: 'var(--space-6) var(--space-4)',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <span style={{ fontSize: '1.5rem' }}>⏱</span>
            <span style={{ fontWeight: 700, fontSize: 'var(--font-size-lg)', color: accent }}>
              PontoFlow
            </span>
          </Link>
          {superadmin && (
            <span
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-warning)',
                marginTop: 'var(--space-1)',
                display: 'block',
              }}
            >
              Painel Super-Admin
            </span>
          )}
        </div>

        {/* Nav items */}
        <nav style={{ flex: 1, padding: 'var(--space-4) 0' }}>
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to.split('/').length <= 2}
              style={({ isActive }) => ({
                display: 'block',
                padding: 'var(--space-3) var(--space-4)',
                fontSize: 'var(--font-size-sm)',
                color: isActive ? accent : 'rgba(255,255,255,0.7)',
                backgroundColor: isActive ? 'rgba(255,255,255,0.05)' : 'transparent',
                borderLeft: isActive ? `3px solid ${accent}` : '3px solid transparent',
                transition: 'all 0.15s',
              })}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* User info + logout */}
        <div
          style={{
            padding: 'var(--space-4)',
            borderTop: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          {user && (
            <div style={{ marginBottom: 'var(--space-3)' }}>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'rgba(255,255,255,0.5)' }}>
                {ROLE_LABELS[user.role]}
              </div>
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'rgba(255,255,255,0.85)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {user.sub.slice(0, 8)}…
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="btn btn-ghost"
            style={{ width: '100%', color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.2)' }}
          >
            Sair
          </button>
        </div>
      </aside>

      {/* Main */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Plan banner (only for tenant users) */}
        {user && user.role !== 'SUPER_ADMIN' && <PlanBanner plan={user.plan} />}

        <main style={{ flex: 1, padding: 'var(--space-8)', overflowY: 'auto' }}>
          <Outlet />
        </main>
      </div>
    </div>
  )
}
